import {
    useState
} from "react";
import {
    useWindowDimensions,
    StyleSheet,
    View
} from "react-native";
import {
    WorkspaceSwitcher
} from "./brand";
import {
    type ColorSwatchPickerItem,
    type NCoreUIKitIcon,
    NCoreUIKitLocalize,
    NCoreUIKitTheme,
    ToggleGroup,
    ColorSwatch,
    RadioButton,
    Breadcrumb,
    ColorField,
    TextInput,
    Separator,
    StatTile,
    CheckBox,
    Sidebar,
    Avatar,
    Button,
    Switch,
    Slider,
    Alert,
    Badge,
    Empty,
    Card,
    Item,
    Tabs,
    Text
} from "ncore-ui-kit";
import {
    useDocsI18n
} from "../i18n";
import {
    EllipsisVertical,
    LayoutDashboard,
    ChartColumn,
    CircleHelp,
    Settings,
    Monitor,
    Folder,
    Users,
    Bell,
    Moon,
    Sun
} from "lucide-react-native";
import {
    createKitIcon
} from "../components/kitIcons";

type Appearance = {
    corners: string;
    density: string;
    palette: string;
    brand: string;
    theme: string;
    font: number;
};

const LayoutDashboardIcon = createKitIcon(LayoutDashboard);

const EllipsisVerticalIcon = createKitIcon(EllipsisVertical);

const ChartColumnIcon = createKitIcon(ChartColumn);

const CircleHelpIcon = createKitIcon(CircleHelp);

const SettingsIcon = createKitIcon(Settings);

const MonitorIcon = createKitIcon(Monitor);

const FolderIcon = createKitIcon(Folder);

const UsersIcon = createKitIcon(Users);

const BellIcon = createKitIcon(Bell);

const MoonIcon = createKitIcon(Moon);

const SunIcon = createKitIcon(Sun);

const SECTIONS = [
    "profile",
    "account",
    "security",
    "notifications",
    "appearance",
    "integrations",
    "billing"
] as const;

const THEMES = [
    {
        icon: SunIcon,
        key: "light"
    },
    {
        icon: MoonIcon,
        key: "dark"
    },
    {
        icon: MonitorIcon,
        key: "system"
    }
] as const;

const PALETTES = [
    {
        dots: [
            "#006155",
            "#ac0051",
            "#10641a",
            "#5180e7"
        ],
        key: "nibgat"
    },
    {
        key: "community",
        dots: [
            "#6436c4",
            "#ac0051",
            "#10641a",
            "#c1236b"
        ]
    },
    {
        dots: [
            "#b10016",
            "#7c4b00",
            "#10641a",
            "#005c85"
        ],
        key: "caycore"
    }
] as const;

const PRESET_COLORS = [
    {
        color: "#009482",
        key: "teal"
    },
    {
        color: "#be3600",
        key: "brick"
    },
    {
        color: "#5180e7",
        key: "blue"
    },
    {
        color: "#826200",
        key: "mustard"
    },
    {
        color: "#b561cd",
        key: "lilac"
    },
    {
        color: "#237700",
        key: "green"
    },
    {
        color: "#0090aa",
        key: "turquoise"
    },
    {
        color: "#c1236b",
        key: "pink"
    }
] as const;

const DENSITIES = [
    "spacious",
    "compact"
] as const;

const CORNERS = [
    "soft",
    "sharp"
] as const;

const DEFAULTS: Appearance = {
    density: "spacious",
    palette: "nibgat",
    brand: "#006155",
    corners: "soft",
    theme: "system",
    font: 105
};

const INITIAL: Appearance = {
    ...DEFAULTS,
    brand: "#3c527f",
    theme: "light",
    font: 100
};

const REVENUE_TREND = [
    38,
    41,
    40,
    46,
    44,
    51,
    49,
    55,
    58,
    57,
    63
];

const FONT_MIN = 90;

const FONT_MAX = 125;

const FONT_STEP = 5;

const REVENUE = 482900;

const stylesheet = StyleSheet.create({
    bar: {
        justifyContent: "space-between",
        alignItems: "center",
        flexDirection: "row",
        flexWrap: "wrap"
    },
    titleRow: {
        justifyContent: "space-between",
        alignItems: "center",
        flexDirection: "row"
    },
    footer: {
        justifyContent: "flex-end",
        flexDirection: "row",
        flexWrap: "wrap"
    },
    column: {
        flexDirection: "column"
    },
    start: {
        alignSelf: "flex-start"
    },
    columns: {
        flexDirection: "row",
        flexWrap: "wrap"
    },
    barRow: {
        alignItems: "center",
        flexDirection: "row"
    },
    wrapRow: {
        alignItems: "center",
        flexDirection: "row",
        flexWrap: "wrap"
    },
    row: {
        flexDirection: "row"
    },
    stretch: {
        alignSelf: "stretch"
    },
    list: {
        width: "auto"
    },
    content: {
        minWidth: 0,
        flex: 1
    },
    option: {
        minWidth: 0,
        flex: 1
    },
    page: {
        minWidth: 0
    }
});

const handlePress = () => undefined;

const Settings02 = () => {
    const {
        t
    } = useDocsI18n();

    const {
        activeLocale
    } = NCoreUIKitLocalize.useContext();

    const {
        shapeConfigs,
        spaces
    } = NCoreUIKitTheme.useContext();

    const {
        width
    } = useWindowDimensions();

    const [
        section,
        setSection
    ] = useState<string>("appearance");

    const [
        saved,
        setSaved
    ] = useState<Appearance>(DEFAULTS);

    const [
        values,
        setValues
    ] = useState<Appearance>(INITIAL);

    const [
        isNotify,
        setIsNotify
    ] = useState(true);

    const [
        isPublic,
        setIsPublic
    ] = useState(true);

    const [
        projectName,
        setProjectName
    ] = useState(t("blocks.settings02.preview.projectValue"));

    const isCompact = width < shapeConfigs.compactBreakpoint;

    const isRoomy = width >= shapeConfigs.wideBreakpoint + shapeConfigs.sideListWidth;

    const changeCount = (Object.keys(values) as Array<keyof Appearance>).filter((key) => values[key] !== saved[key]).length;

    const currency = new Intl.NumberFormat(activeLocale, {
        maximumFractionDigits: 0,
        style: "currency",
        currency: "TRY"
    });

    const presets: Array<ColorSwatchPickerItem> = PRESET_COLORS.map((preset) => ({
        title: t(`blocks.settings02.colors.${preset.key}`),
        color: preset.color
    }));

    const handleChange = <Key extends keyof Appearance>(key: Key) => (value: Appearance[Key]) => setValues({
        ...values,
        [key]: value
    });

    const handleToggleChange = (key: "corners" | "density" | "theme") => (value: string | Array<string>) => {
        if(typeof value === "string") {
            setValues({
                ...values,
                [key]: value
            });
        }
    };

    const handleFontChange = (value: number | [number, number]) => {
        if(typeof value === "number") {
            setValues({
                ...values,
                font: value
            });
        }
    };

    const handleReset = () => setValues(DEFAULTS);

    const handleSave = () => setSaved(values);

    const renderNavItem = (title: string, icon: NCoreUIKitIcon, isActive: boolean = false) => <Sidebar.MenuItem>
        <Sidebar.MenuButton
            onPress={handlePress}
            isActive={isActive}
            title={title}
            icon={icon}
        />
    </Sidebar.MenuItem>;

    const renderHeading = (title: string, description: string) => <View
        style={[
            stylesheet.column,
            {
                gap: spaces.spacingXs
            }
        ]}
    >
        <Text
            variant="titleMediumSize"
            color="high"
        >
            {title}
        </Text>
        <Text
            variant="bodyMediumSize"
            color="mid"
        >
            {description}
        </Text>
    </View>;

    const renderToggle = (key: "corners" | "density" | "theme", options: ReadonlyArray<{
        icon?: NCoreUIKitIcon;
        key: string;
    }>) => <ToggleGroup
        title={t(`blocks.settings02.${key}.title`)}
        onValueChange={handleToggleChange(key)}
        attachBehaviour="attached"
        style={stylesheet.start}
        value={values[key]}
        variant="outline"
    >
        {
            options.map((option) => <ToggleGroup.Item
                title={t(`blocks.settings02.${key}.${option.key}`)}
                icon={option.icon}
                value={option.key}
                key={option.key}
            />)
        }
    </ToggleGroup>;

    const renderPalette = (palette: typeof PALETTES[number]) => <Item
        onPress={() => handleChange("palette")(palette.key)}
        isSelected={values.palette === palette.key}
        style={stylesheet.option}
        variant="outline"
        key={palette.key}
    >
        <Item.Content>
            <Item.Title>
                {t(`blocks.settings02.palette.${palette.key}`)}
            </Item.Title>
        </Item.Content>
        <Item.Actions>
            <RadioButton
                accessibilityLabel={t(`blocks.settings02.palette.${palette.key}`)}
                onPress={() => handleChange("palette")(palette.key)}
                isChecked={values.palette === palette.key}
            />
        </Item.Actions>
        <Item.Footer>
            <View
                style={[
                    stylesheet.row,
                    {
                        gap: spaces.spacingXs
                    }
                ]}
            >
                {
                    palette.dots.map((dot) => <ColorSwatch
                        size="small"
                        color={dot}
                        key={dot}
                    />)
                }
            </View>
        </Item.Footer>
    </Item>;

    const renderSettings = () => <Card
        style={[
            stylesheet.content,
            {
                flexBasis: shapeConfigs.dialogMaxWidth
            }
        ]}
        variant="outline"
    >
        <Card.Content>
            <View
                style={[
                    stylesheet.column,
                    {
                        gap: spaces.spacingLg
                    }
                ]}
            >
                {renderHeading(t("blocks.settings02.theme.title"), t("blocks.settings02.theme.description"))}
                {renderToggle("theme", THEMES)}
                <Separator/>
                {renderHeading(t("blocks.settings02.palette.title"), t("blocks.settings02.palette.description"))}
                <View
                    style={[
                        isCompact ? stylesheet.column : stylesheet.row,
                        {
                            gap: spaces.spacingSm
                        }
                    ]}
                    aria-label={t("blocks.settings02.palette.title")}
                    role="radiogroup"
                >
                    {PALETTES.map(renderPalette)}
                </View>
                <Separator/>
                {renderHeading(t("blocks.settings02.brand.title"), t("blocks.settings02.brand.description"))}
                <ColorField
                    title={t("blocks.settings02.brand.field")}
                    onChange={handleChange("brand")}
                    spreadBehaviour="stretch"
                    isContrastShown={true}
                    value={values.brand}
                    presets={presets}
                />
                <Separator/>
                <View
                    style={[
                        isCompact ? stylesheet.column : stylesheet.row,
                        {
                            gap: spaces.spacingLg
                        }
                    ]}
                >
                    <View
                        style={[
                            stylesheet.column,
                            stylesheet.option,
                            {
                                gap: spaces.spacingMd
                            }
                        ]}
                    >
                        {renderHeading(t("blocks.settings02.density.title"), t("blocks.settings02.density.description"))}
                        {renderToggle("density", DENSITIES.map((key) => ({
                            key
                        })))}
                    </View>
                    <View
                        style={[
                            stylesheet.column,
                            stylesheet.option,
                            {
                                gap: spaces.spacingMd
                            }
                        ]}
                    >
                        {renderHeading(t("blocks.settings02.corners.title"), t("blocks.settings02.corners.description"))}
                        {renderToggle("corners", CORNERS.map((key) => ({
                            key
                        })))}
                    </View>
                </View>
                <Separator/>
                <Slider
                    title={t("blocks.settings02.font.title")}
                    formatValueText={(value) => `%${value}`}
                    onValueChange={handleFontChange}
                    isShowMinMaxLabels={true}
                    value={values.font}
                    step={FONT_STEP}
                    min={FONT_MIN}
                    max={FONT_MAX}
                />
            </View>
        </Card.Content>
    </Card>;

    const renderPreview = () => <Card
        style={[
            stylesheet.content,
            {
                flexBasis: shapeConfigs.drawerWidth
            }
        ]}
        variant="outline"
    >
        <Card.Header>
            <Card.Title>
                {t("blocks.settings02.preview.title")}
            </Card.Title>
            <Card.Description>
                {t("blocks.settings02.preview.description")}
            </Card.Description>
            <Card.Action>
                <Badge
                    title={t("blocks.settings02.preview.live")}
                    variant="tonal"
                    type="success"
                    size="small"
                />
            </Card.Action>
        </Card.Header>
        <Card.Content>
            <View
                style={[
                    stylesheet.column,
                    {
                        gap: spaces.spacingMd
                    }
                ]}
            >
                <StatTile
                    trend={REVENUE_TREND}
                    isTrendFlush={true}
                    variant="filled"
                >
                    <StatTile.Title>
                        {t("blocks.settings02.preview.revenue")}
                    </StatTile.Title>
                    <StatTile.Value>
                        {currency.format(REVENUE)}
                    </StatTile.Value>
                    <StatTile.Delta
                        description={t("blocks.settings02.preview.revenueTrend")}
                        value={0.184}
                    />
                </StatTile>
                <TextInput
                    title={t("blocks.settings02.preview.project")}
                    customBorderColor={values.brand}
                    onChangeText={setProjectName}
                    spreadBehaviour="stretch"
                    value={projectName}
                />
                <CheckBox
                    title={t("blocks.settings02.preview.notify")}
                    onCheckedChange={setIsNotify}
                    isChecked={isNotify}
                />
                <Switch
                    title={t("blocks.settings02.preview.public")}
                    onCheckedChange={setIsPublic}
                    spreadBehaviour="stretch"
                    isChecked={isPublic}
                    isFlip={true}
                />
                <View
                    style={[
                        stylesheet.wrapRow,
                        {
                            gap: spaces.spacingSm
                        }
                    ]}
                >
                    <Badge
                        title={t("blocks.settings02.preview.draft")}
                        variant="outline"
                        type="neutral"
                    />
                    <Badge
                        title={t("blocks.settings02.preview.campaign")}
                        customColor={values.brand}
                        variant="solid"
                    />
                    <Badge
                        title={t("blocks.settings02.preview.published")}
                        variant="tonal"
                        type="success"
                    />
                </View>
                <View
                    style={[
                        stylesheet.footer,
                        {
                            gap: spaces.spacingSm
                        }
                    ]}
                >
                    <Button
                        title={t("blocks.settings02.preview.cancel")}
                        spreadBehaviour="free"
                        onPress={handlePress}
                        variant="outline"
                        type="neutral"
                    />
                    <Button
                        title={t("blocks.settings02.preview.publish")}
                        customColor={values.brand}
                        spreadBehaviour="free"
                        onPress={handlePress}
                        variant="filled"
                    />
                </View>
                <Alert
                    title={t("blocks.settings02.preview.notice")}
                    type="success"
                />
            </View>
        </Card.Content>
    </Card>;

    const renderAppearance = () => <View
        style={[
            stylesheet.columns,
            {
                gap: spaces.spacingLg
            }
        ]}
    >
        {renderSettings()}
        {renderPreview()}
    </View>;

    const renderOther = () => <Empty
        variant="outline"
    >
        <Empty.Header>
            <Empty.Media
                icon={SettingsIcon}
            />
            <Empty.Title>
                {t(`blocks.settings02.sections.${section}`)}
            </Empty.Title>
            <Empty.Description>
                {t("blocks.settings02.otherDescription")}
            </Empty.Description>
        </Empty.Header>
    </Empty>;

    return <Sidebar.Provider
        variant="inset"
    >
        <Sidebar
            collapseBehaviour={isRoomy ? "offcanvas" : "icon"}
            title={t("blocks.settings02.navTitle")}
        >
            <Sidebar.Header>
                <WorkspaceSwitcher
                    onPress={handlePress}
                />
            </Sidebar.Header>
            <Sidebar.Content>
                <Sidebar.Group>
                    <Sidebar.Menu>
                        {renderNavItem(t("blocks.settings02.navOverview"), LayoutDashboardIcon)}
                        <Sidebar.MenuItem>
                            <Sidebar.MenuButton
                                title={t("blocks.settings02.navProjects")}
                                onPress={handlePress}
                                icon={FolderIcon}
                            />
                            <Sidebar.MenuBadge
                                count={12}
                            />
                        </Sidebar.MenuItem>
                        {renderNavItem(t("blocks.settings02.navTeam"), UsersIcon)}
                        {renderNavItem(t("blocks.settings02.navReports"), ChartColumnIcon)}
                    </Sidebar.Menu>
                </Sidebar.Group>
                <Sidebar.Group
                    title={t("blocks.settings02.navAccount")}
                >
                    <Sidebar.Menu>
                        {renderNavItem(t("blocks.settings02.navSettings"), SettingsIcon, true)}
                        {renderNavItem(t("blocks.settings02.navHelp"), CircleHelpIcon)}
                    </Sidebar.Menu>
                </Sidebar.Group>
            </Sidebar.Content>
            <Sidebar.Footer>
                <Sidebar.Menu>
                    <Sidebar.MenuItem>
                        <Sidebar.MenuButton
                            renderIcon={() => <Avatar
                                title={t("blocks.settings02.userName")}
                                size="xSmall"
                            />}
                            subTitle={t("blocks.settings02.userEmail")}
                            title={t("blocks.settings02.userName")}
                        />
                        <Sidebar.MenuAction
                            title={t("blocks.settings02.userMenu")}
                            icon={EllipsisVerticalIcon}
                            onPress={handlePress}
                            isShowOnHover={false}
                        />
                    </Sidebar.MenuItem>
                </Sidebar.Menu>
            </Sidebar.Footer>
        </Sidebar>
        <Sidebar.Inset>
            <View
                style={[
                    stylesheet.bar,
                    {
                        paddingHorizontal: isCompact ? spaces.spacingMd : spaces.spacingLg,
                        paddingVertical: spaces.spacingMd,
                        gap: spaces.spacingSm
                    }
                ]}
            >
                <View
                    style={[
                        stylesheet.barRow,
                        {
                            gap: spaces.spacingSm
                        }
                    ]}
                >
                    <Sidebar.Trigger/>
                    <Breadcrumb
                        accessibilityLabel={t("blocks.settings02.breadcrumb")}
                    >
                        <Breadcrumb.List>
                            <Breadcrumb.Item>
                                <Breadcrumb.Link
                                    title={t("blocks.settings02.navSettings")}
                                    onPress={handlePress}
                                />
                            </Breadcrumb.Item>
                            <Breadcrumb.Item>
                                <Breadcrumb.Separator/>
                                <Breadcrumb.Page
                                    title={t("blocks.settings02.sections.appearance")}
                                />
                            </Breadcrumb.Item>
                        </Breadcrumb.List>
                    </Breadcrumb>
                </View>
                <View
                    style={[
                        stylesheet.wrapRow,
                        {
                            gap: spaces.spacingSm
                        }
                    ]}
                >
                    <Text
                        variant="bodySmallSize"
                        color="mid"
                    >
                        {changeCount > 0 ? t("blocks.settings02.unsaved", [
                            changeCount
                        ]) : t("blocks.settings02.allSaved")}
                    </Text>
                    <Button
                        title={t("blocks.settings02.reset")}
                        spreadBehaviour="free"
                        onPress={handleReset}
                        variant="ghost"
                        type="neutral"
                        size="small"
                    />
                    <Button
                        title={t("blocks.settings02.save")}
                        isDisabled={changeCount === 0}
                        spreadBehaviour="free"
                        onPress={handleSave}
                        variant="filled"
                        type="primary"
                        size="small"
                    />
                    <Badge.Indicator
                        type="danger"
                    >
                        <Button
                            accessibilityLabel={t("blocks.settings02.notifications")}
                            onPress={handlePress}
                            variant="ghost"
                            icon={BellIcon}
                            type="neutral"
                            size="small"
                        />
                    </Badge.Indicator>
                </View>
            </View>
            <Separator/>
            <View
                style={[
                    stylesheet.page,
                    {
                        padding: isCompact ? spaces.spacingMd : spaces.spacingLg,
                        gap: spaces.spacingLg
                    }
                ]}
            >
                <View
                    style={[
                        stylesheet.column,
                        {
                            gap: spaces.spacingXs
                        }
                    ]}
                >
                    <Text
                        variant="headlineMediumSize"
                        color="high"
                    >
                        {t("blocks.settings02.title")}
                    </Text>
                    <Text
                        variant="bodyMediumSize"
                        color="mid"
                    >
                        {t("blocks.settings02.description")}
                    </Text>
                </View>
                <Tabs
                    orientation={isCompact ? "horizontal" : "vertical"}
                    overflowBehaviour="scroll"
                    onValueChange={setSection}
                    style={stylesheet.start}
                    value={section}
                    variant="line"
                >
                    <Tabs.List
                        style={isCompact ? undefined : stylesheet.list}
                        title={t("blocks.settings02.tabsTitle")}
                    >
                        {
                            SECTIONS.map((key) => <Tabs.Trigger
                                title={t(`blocks.settings02.sections.${key}`)}
                                value={key}
                                key={key}
                            />)
                        }
                    </Tabs.List>
                    {
                        SECTIONS.map((key) => <Tabs.Content
                            style={stylesheet.content}
                            value={key}
                            key={key}
                        >
                            {key === "appearance" ? renderAppearance() : renderOther()}
                        </Tabs.Content>)
                    }
                </Tabs>
            </View>
        </Sidebar.Inset>
    </Sidebar.Provider>;
};
export default Settings02;
